/**
 * CONTRACT.md §2.12 — the studio task board.
 *
 * Four columns by default. A board whose columns *are* a variable set — the six
 * clients on `studio/tasks.html` — is `board board--wide` with the count in
 * `--board-cols`, which is §4.1's **third and last** sanctioned inline value.
 * It is not a licence for a page-level `<style>` block: there are none across
 * the page set, and that is the number.
 *
 * `cols` clamps to 1–12. Twelve is the point past which no screen shows a
 * column, and a `--board-cols:0` from an empty list would collapse the grid to
 * nothing rather than to one column.
 *
 * Wide boards are **one row**, and the row scrolls. They used to wrap — `auto-fill`
 * at a fixed min width, extra columns dropping to the next row — and on a seven-client
 * task board that put a whole column three thousand pixels down the page, under a row
 * that looked like the whole board. A column nobody scrolls to is a column nobody
 * reads, and a wrapped one does not even say it is there. `BoardWrap` is the scroller
 * and carries the same measured note a wide table does (R3-18): drawn when the
 * content really is wider than the box, which in jsdom is never.
 *
 * `boardScrolls` stays false and stays for its callers: the hero's own note counted
 * *columns*, which is a guess about a viewport, and the measured one below is the
 * answer to the same question.
 */

import type { CSSProperties, ReactNode } from 'react'

import { cx, type Common } from './common.js'
import { useCardScrollCue } from './Layout.js'

const MIN_COLS = 1
const MAX_COLS = 12
/**
 * Kept for call sites that once compared against a sideways-scroll threshold.
 * Wrapping replaced that behaviour; the helper always answers false.
 */
export const BOARD_SCROLLS_ABOVE = 4

export type BoardWrapProps = Common & { readonly children: ReactNode }

/** The scroller a wide board's one row of columns needs, and the note under it. */
export function BoardWrap({ children, ...common }: BoardWrapProps) {
  const [ref, cue] = useCardScrollCue(children, true)
  return (
    /*
     * `tabIndex`: a row of columns wider than the window is genuinely scrollable, and
     * a `<div>` scroller with no way to reach it by keyboard fails WCAG 2.1.1 —
     * axe's `scrollable-region-focusable`, the same rule `ScrollX` carries it for.
     */
    <div className="board-wrap" ref={ref} tabIndex={0} {...common}>
      {children}
      {cue}
    </div>
  )
}

export type BoardProps = Common & {
  /** Given, the board is `.board--wide` with this many columns. Clamped to 1–12. */
  readonly cols?: number | undefined
  readonly children: ReactNode
}

export function Board({ cols, children, ...common }: BoardProps) {
  /*
   * A NaN would reach the stylesheet as `repeat(NaN, …)`, which the browser
   * drops — leaving `.board--wide` with no columns at all and the board falling
   * back to four without anybody noticing. It is the four-column board here,
   * which is the same picture and an honest one.
   */
  if (cols === undefined || !Number.isFinite(cols)) {
    return (
      <div className="board" {...common}>
        {children}
      </div>
    )
  }
  const clamped = Math.min(Math.max(cols, MIN_COLS), MAX_COLS)
  return (
    <div
      className="board board--wide"
      style={{ '--board-cols': clamped } as CSSProperties}
      {...common}
    >
      {children}
    </div>
  )
}

/** Always false — the note a wide board owes is measured by `BoardWrap` itself. */
export function boardScrolls(_cols: number | undefined): boolean {
  return false
}

export type BoardColProps = Common & { readonly children: ReactNode }

export function BoardCol({ children, ...common }: BoardColProps) {
  return (
    <div className="board__col" {...common}>
      {children}
    </div>
  )
}

export type BoardHeadProps = Common & {
  readonly children: ReactNode
  /** The count at the right of the head. `.n` is mono and pushed by app.css. */
  readonly count?: number | undefined
}

export function BoardHead({ children, count, ...common }: BoardHeadProps) {
  return (
    <div className="board__head" {...common}>
      {children}
      {count === undefined ? null : <span className="n">{count}</span>}
    </div>
  )
}

export type TaskCardState = 'need' | 'bad'

/** Board "What to do" preview — full text stays on the element's `title` for hover. */
export const NOTE_PREVIEW_MAX = 100

export function notePreview(text: string, max = NOTE_PREVIEW_MAX): string {
  if (text.length <= max) return text
  return `${text.slice(0, max).trimEnd()}…`
}

export type TaskCardProps = Common & {
  /** `need` — the next move is the client's. `bad` — genuinely broken: late, failed. */
  readonly state?: TaskCardState | undefined
  /** The service or the ref the task is under — "Video editing · REQ-119". */
  readonly client: ReactNode
  readonly title: ReactNode
  /** The `.xs muted` line under the title, when there is one. */
  readonly note?: ReactNode | undefined
  /** The bottom row: an `Avatar` and whatever else the card carries. */
  readonly meta: ReactNode
}

export function TaskCard({ state, client, title, note, meta, ...common }: TaskCardProps) {
  return (
    <div
      className={cx('tcard', state === 'need' && 'tcard--need', state === 'bad' && 'tcard--bad')}
      {...common}
    >
      <div className="tcard__client">{client}</div>
      <div className="tcard__title">{title}</div>
      {note === undefined ? null : (
        <div
          className="tcard__note xs muted"
          /*
           * Full string on `title` when the note is text — the preview is capped at
           * NOTE_PREVIEW_MAX characters (CSS still clamps to two lines). A ReactNode
           * note is the caller's own markup; we do not invent a tooltip for it.
           */
          {...(typeof note === 'string' ? { title: note } : {})}
        >
          {typeof note === 'string' ? notePreview(note) : note}
        </div>
      )}
      <div className="tcard__meta">{meta}</div>
    </div>
  )
}

export type AvatarProps = Common & {
  /** The initials. */
  readonly children: ReactNode
  /** Whose they are. Two letters mean nothing read aloud. */
  readonly name: string
}

export function Avatar({ children, name, ...common }: AvatarProps) {
  return (
    <span className="avatar" title={name} aria-label={name} role="img" {...common}>
      {children}
    </span>
  )
}

export type BoardScrollNoteProps = Common & { readonly children: ReactNode }

export function BoardScrollNote({ children, ...common }: BoardScrollNoteProps) {
  return (
    <span className="board-scroll-note" {...common}>
      {children}
    </span>
  )
}
